import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { CustomActiveCartService } from '@cart/data-access';
import { CookieKeys } from '@core/const';
import { CookieService } from '@core/feature';
import { Observable, Subject, Subscription, interval } from 'rxjs';
import { debounce, filter, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class CheckoutInterceptor implements HttpInterceptor, OnDestroy {
  debounceCartRefresher$: Subject<HttpRequest<any>> = new Subject();
  addRefreshParam: boolean;
  checkoutCalculationUrls: Array<string> = [
    'delivery/',
    '/addresses/delivery?',
    '/deliverymode?',
    '/deliverymodes',
    '/pickup/pos?',
    '/customerconsignment',
  ];
  checkoutRefreshFilter = /(?:carts)\/\S+(?:\/)/gm;
  subscription = new Subscription();
  isCheckoutV2Enabled = this.cookieService.hasKey(CookieKeys.ChekoutV2Enabled);

  constructor(
    private activeCartService: CustomActiveCartService,
    private cookieService: CookieService,
  ) {
    if (this.isCheckoutV2Enabled) {
      return;
    }

    this.subscription.add(
      this.debounceCartRefresher$
        .pipe(
          filter((request) => this.isCheckoutRequest(request)),
          debounce(() => interval(1000)),
          tap(() => {
            this.addRefreshParam = true;
            this.activeCartService.refreshCurrentCart();
          }),
        )
        .subscribe(),
    );
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isCheckoutV2Enabled) {
      return next.handle(request);
    }

    let requestToHandle = request;

    if (this.isCartRefreshRequest(request) && this.addRefreshParam) {
      requestToHandle = request.clone({
        params: request.params.set('refreshCart', `${this.addRefreshParam}`),
      });
      this.addRefreshParam = false;
    }

    return next.handle(requestToHandle).pipe(
      tap(() => {
        this.debounceCartRefresher$.next(requestToHandle);
      }),
    );
  }

  isCheckoutRequest(request: HttpRequest<any>): boolean {
    return this.checkoutCalculationUrls.some((checkoutUrlSubstring) => request.url.includes(checkoutUrlSubstring));
  }

  isCartRefreshRequest(request: HttpRequest<any>): boolean {
    return request.method === 'GET' && !this.checkoutRefreshFilter.test(request.url);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
